import { Middleware } from '@nuxt/types';
import { redirectToFallback } from '~/helpers/notFoundFallback';
import { CustomRoutableInterface } from '~/modules/GraphQL/custom-types';
import { useAnalysesStore } from '~/modules/catalog/product/stores/analyses';
import { usePageStore } from '~/stores/page';

/* eslint-disable unicorn/prefer-ternary */

// Navigation Structure
// /analyses : Homepage > Analyses
// /analyses/analysis-url : Homepage > Analyses > AnalysisName
// /product-url/analyses : Homepage > ProductName > Analyses
// /product-url/analyses/analysis-url : Homepage > ProductName > ProductName Analyses > AnalysisName

interface RouteUrl {
  parentRoute?: string;
  route?: string;
}

const urlResolverMiddleware: Middleware = async (context) => {
  const listingPageRoute = 'analys';
  const pageStore = usePageStore(context.$pinia);
  const analysesStore = useAnalysesStore(context.$pinia);
  const { path } = context.route;
  const validLocales = ['en', 'sv'];

  const parts = path.split('/'); // ['', 'locale', ...]
  if (validLocales.includes(parts[1])) {
    parts.splice(1, 1); // remove locale if it exists
  }
  const firstPart = parts[1];
  const secondPart = parts[2];
  const thirdPart = parts[3];

  // This is used to reset the store when navigating from a product to another product
  // If new ways of navigating to the analyses page are added, this should be updated
  if (firstPart === listingPageRoute && !secondPart) {
    // Bare /analys has no page of its own (it used to fail with a 500); the list lives at /alla-analyser
    context.redirect(301, context.app.localePath('/alla-analyser'));
    return;
  }

  let routeUrls: RouteUrl = {};

  if (thirdPart) {
    // Case for route of specific analysis from a package's list (/product-url/analyses/analysis-url)
    routeUrls = { parentRoute: firstPart, route: thirdPart };
  } else if (firstPart === listingPageRoute) {
    // We can also have (/analyses/analysis-url)
    routeUrls = { route: secondPart };
  } else if (secondPart === listingPageRoute) {
    // (/product-url/analyses)
    routeUrls = { parentRoute: firstPart };
  }

  if (routeUrls.parentRoute && routeUrls.parentRoute !== listingPageRoute && !routeUrls.route) {
    // List of all analyses belonging to a given package (/analys)
    pageStore.$patch((state) => {
      state.routeData = null;
    });
  }

  if (routeUrls.parentRoute && pageStore.parentRouteData?.slug !== routeUrls.parentRoute) {
    const { data, errors } = await context.app.$vsf.$magento.api.route(routeUrls.parentRoute);

    const results: CustomRoutableInterface | null = data?.route ?? null;

    if (!results || errors?.length) {
      redirectToFallback(context);
      return;
    }

    if (pageStore.parentRouteData?.sku !== results?.sku) {
      analysesStore.reset();
    }

    pageStore.$patch((state) => {
      state.parentRouteData = { ...results, slug: routeUrls.parentRoute };
      state.routeData = null;
    });
  } else if (!routeUrls.parentRoute) {
    pageStore.$patch((state) => {
      state.parentRouteData = null;
      state.routeData = null;
    });
  }

  if (routeUrls.route) {
    const { data, errors } = await context.app.$vsf.$magento.api.route(routeUrls.route);

    const results: CustomRoutableInterface | null = data?.route ?? null;

    if (!results || errors?.length) {
      redirectToFallback(context);
      return;
    }

    pageStore.$patch((state) => {
      state.routeData = results;
    });
  }
};

export default urlResolverMiddleware;
